import { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isSameMonth, parseISO, startOfYear, endOfYear, startOfDay, endOfDay } from 'date-fns';
import { ar } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { db } from '@/lib/firebase';
import { collection, addDoc, query, where, getDocs, orderBy, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { useToast } from '@/components/ui/use-toast';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Search, Phone, User, Stethoscope, MessageSquare, Trash2, CreditCard, Plus, Pencil, Download, Printer, BarChart3, TrendingUp, DollarSign, Users, Clock, UserCheck, UserX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import * as XLSX from 'xlsx';
import { Timestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import QueueSystem from '@/components/QueueSystem';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

interface Appointment {
  id: string;
  patientName: string;
  phone: string;
  serviceType: string;
  date: Date;
  time: string;
  price: number;
  paymentStatus: 'paid' | 'pending' | 'transferred';
  remainingAmount: number;
  createdAt: Date;
  notes: string;
  queueStatus?: 'waiting' | 'in-progress' | 'completed' | 'cancelled';
}

interface Patient {
  id: string;
  name: string;
  phone: string;
  remainingAmount: number;
  isFromAppointments?: boolean;
}

const serviceTypes = [
  "كشف أولي",
  "كشف متابعة",
  "تنظيف أسنان",
  "حشو",
  "علاج عصب",
  "خلع",
  "تقويم",
  "تبييض",
  "تركيب",
  "زراعة",
  "أشعة",
  "أخرى"
];

export default function ClinicAppointments() {
  const { toast } = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isAddingAppointment, setIsAddingAppointment] = useState(false);
  const [filter, setFilter] = useState<'day' | 'month' | 'year'>('day');
  const [searchQuery, setSearchQuery] = useState('');
  const [newAppointment, setNewAppointment] = useState<Partial<Appointment>>({
    patientName: '',
    phone: '',
    serviceType: '',
    date: new Date(),
    time: '',
    price: 0,
    paymentStatus: 'pending',
    remainingAmount: 0,
    notes: ''
  });
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [dateRange, setDateRange] = useState<{
    from: Date;
    to: Date;
  }>({
    from: startOfDay(new Date()),
    to: endOfDay(new Date())
  });
  const [filterType, setFilterType] = useState<'month' | 'day' | 'year' | 'all'>('day');
  const [statistics, setStatistics] = useState({
    totalAppointments: 0,
    totalRevenue: 0,
    averagePrice: 0,
    topService: '',
    topServiceCount: 0
  });
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [showQueueSystem, setShowQueueSystem] = useState(false);
  const [activeTab, setActiveTab] = useState<'today' | 'archive'>('today');
  const [archiveDate, setArchiveDate] = useState<Date>(new Date());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [archiveDateRange, setArchiveDateRange] = useState<Date>(new Date());
  const [originalAppointmentsOrder, setOriginalAppointmentsOrder] = useState<string[]>([]);

  useEffect(() => {
    console.log('Component mounted or dependencies changed');
    console.log('Current filter type:', filterType);
    console.log('Current date range:', dateRange);
    
    // تحديث تاريخ الأرشيف ليكون نفس التاريخ المختار (للكشوفات المنتهية من اليوم الحالي)
    if (canShowArchive()) {
      setArchiveDateRange(dateRange.from);
    }
    
    // إعادة تعيين الترتيب الأصلي للكشوفات عند تغيير التاريخ
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateRange.from);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate.getTime() !== today.getTime()) {
      // إعادة تعيين الترتيب الأصلي للكشوفات
      const originalOrder = appointments
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map(app => app.id);
      setOriginalAppointmentsOrder(originalOrder);
    }
    
    fetchAppointments();
    fetchPatients();
  }, [filterType, dateRange, activeTab]);

  const fetchAppointments = async () => {
    try {
      console.log('Starting to fetch appointments...');
      
      // First, try to fetch all appointments without filters
      const appointmentsRef = collection(db, 'clinicAppointments');
      const allQuery = query(appointmentsRef);
      const allSnapshot = await getDocs(allQuery);
      
      console.log('Total appointments in collection:', allSnapshot.size);
      
      if (allSnapshot.size === 0) {
        console.log('No appointments found in the collection');
        setAppointments([]);
        calculateStatistics([]);
        return;
      }

      // Now apply filters if needed
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      if (filterType !== 'all') {
        switch (filterType) {
          case 'day':
            startDate = startOfDay(dateRange.from);
            endDate = endOfDay(dateRange.from);
            break;
          case 'month':
            startDate = startOfMonth(dateRange.from);
            endDate = endOfMonth(dateRange.from);
            break;
          case 'year':
            startDate = startOfYear(dateRange.from);
            endDate = endOfYear(dateRange.from);
            break;
        }
      }

      console.log('Fetching appointments with filter:', filterType);
      console.log('Date range:', { startDate, endDate });

      let baseQuery = query(appointmentsRef, orderBy('createdAt', 'desc'));

      if (startDate && endDate) {
        baseQuery = query(
          appointmentsRef,
          where('createdAt', '>=', Timestamp.fromDate(startDate)),
          where('createdAt', '<=', Timestamp.fromDate(endDate)),
          orderBy('createdAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(baseQuery);
      console.log('Filtered appointments count:', querySnapshot.size);

      const appointmentsData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Document data:', data);
        
        // Safely handle date fields
        const date = data.date instanceof Timestamp ? data.date.toDate() : new Date();
        const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date();
        
        return {
          id: doc.id,
          patientName: data.patientName as string || '',
          phone: data.phone as string || '',
          serviceType: data.serviceType as string || '',
          date: date,
          time: data.time as string || '',
          price: Number(data.price) || 0,
          paymentStatus: data.paymentStatus as 'paid' | 'pending' | 'transferred' || 'pending',
          remainingAmount: Number(data.remainingAmount) || 0,
          createdAt: createdAt,
          notes: data.notes as string || '',
          queueStatus: data.queueStatus as 'waiting' | 'in-progress' | 'completed' | 'cancelled' || 'waiting'
        } as Appointment;
      });

      console.log('Processed appointments:', appointmentsData);
      setAppointments(appointmentsData);
      
      // حفظ الترتيب الأصلي للكشوفات (حسب وقت الإنشاء)
      const originalOrder = appointmentsData
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map(app => app.id);
      setOriginalAppointmentsOrder(originalOrder);
      
      calculateStatistics(appointmentsData);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء جلب الكشوفات"
      });
    }
  };

  const fetchPatients = async () => {
    try {
      // Get all patients from patients collection
      const patientsRef = collection(db, 'patients');
      const patientsSnapshot = await getDocs(patientsRef);
      const patientsData = patientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Patient[];

      // Get all unique patients from appointments
      const appointmentsRef = collection(db, 'clinicAppointments');
      const appointmentsSnapshot = await getDocs(appointmentsRef);
      const appointmentsData = appointmentsSnapshot.docs.map(doc => doc.data());

      // Create a map of unique patients from appointments
      const uniquePatients = new Map();
      appointmentsData.forEach(appointment => {
        if (appointment.patientName && appointment.phone) {
          const key = `${appointment.patientName}-${appointment.phone}`;
          if (!uniquePatients.has(key)) {
            uniquePatients.set(key, {
              id: `appointment-${key}`,
              name: appointment.patientName,
              phone: appointment.phone,
              remainingAmount: appointment.remainingAmount || 0,
              isFromAppointments: true
            });
          }
        }
      });

      // Combine both lists, giving priority to patients from patients collection
      const allPatients = [
        ...patientsData,
        ...Array.from(uniquePatients.values())
      ];

      // Remove duplicates based on phone number
      const uniquePatientsByPhone = new Map();
      allPatients.forEach(patient => {
        if (!uniquePatientsByPhone.has(patient.phone)) {
          uniquePatientsByPhone.set(patient.phone, patient);
        }
      });

      setPatients(Array.from(uniquePatientsByPhone.values()));
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء جلب بيانات المرضى"
      });
    }
  };

  const calculateStatistics = (data: Appointment[]) => {
    const serviceCounts: { [key: string]: number } = {};
    let totalPaidRevenue = 0;

    data.forEach(appointment => {
      serviceCounts[appointment.serviceType] = (serviceCounts[appointment.serviceType] || 0) + 1;

      if (appointment.paymentStatus === 'paid') {
        totalPaidRevenue += Number(appointment.price) || 0;
      } else if (appointment.paymentStatus === 'pending') {
        totalPaidRevenue += (Number(appointment.price) || 0) - (Number(appointment.remainingAmount) || 0);
      }
    });

    const topService = Object.entries(serviceCounts)
      .sort(([, a], [, b]) => b - a)[0] || ['', 0];

    setStatistics({
      totalAppointments: data.length,
      totalRevenue: totalPaidRevenue,
      averagePrice: data.length ? totalPaidRevenue / data.length : 0,
      topService: topService[0],
      topServiceCount: topService[1]
    });
  };

  const exportToExcel = () => {
    const data = appointments.map(appointment => ({
      'اسم المريض': appointment.patientName,
      'رقم الهاتف': appointment.phone,
      'نوع الخدمة': appointment.serviceType,
      'السعر': appointment.price,
      'حالة الدفع': appointment.paymentStatus === 'paid' ? 'مدفوع' :
                    appointment.paymentStatus === 'transferred' ? 'تم تحويله' :
                    `باقي عليه ${appointment.remainingAmount} جنيه`,
      'الملاحظات': appointment.notes || '',
      'التاريخ': format(appointment.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'كشوفات العيادة');
    XLSX.writeFile(wb, `كشوفات_العيادة_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const printReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const content = `
      <html dir="rtl">
        <head>
          <title>تقرير كشوفات العيادة</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 20px; }
            .stat-card { border: 1px solid #ddd; padding: 10px; border-radius: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: right; }
            th { background-color: #f5f5f5; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>تقرير كشوفات العيادة</h1>
            <p>الفترة: ${filterType === 'all' ? 'جميع الكشوفات' : `${format(dateRange.from, 'dd MMMM yyyy', { locale: ar })}${filterType !== 'day' ? ` - ${format(dateRange.to, 'dd MMMM yyyy', { locale: ar })}` : ''}`}</p>
          </div>
          
          <div class="stats">
            <div class="stat-card">
              <h3>إجمالي الكشوفات</h3>
              <p>${statistics.totalAppointments}</p>
            </div>
            <div class="stat-card">
              <h3>إجمالي الإيرادات</h3>
              <p>${statistics.totalRevenue.toFixed(2)} جنيه</p>
            </div>
            <div class="stat-card">
              <h3>متوسط سعر الكشف</h3>
              <p>${statistics.averagePrice.toFixed(2)} جنيه</p>
            </div>
            <div class="stat-card">
              <h3>أعلى خدمة</h3>
              <p>${statistics.topService} (${statistics.topServiceCount} كشف)</p>
            </div>
          </div>

          <div className="overflow-x-auto p-2">
            <table className="min-w-full text-sm sm:text-xs">
              <thead>
                <tr>
                  <th>اسم المريض</th>
                  <th>رقم الهاتف</th>
                  <th>نوع الخدمة</th>
                  <th>السعر</th>
                  <th>حالة الدفع</th>
                  <th>الملاحظات</th>
                  <th>التاريخ</th>
                </tr>
              </thead>
              <tbody>
                ${appointments.map(appointment => `
                  <tr>
                    <td>${appointment.patientName}</td>
                    <td>${appointment.phone}</td>
                    <td>${appointment.serviceType}</td>
                    <td>${appointment.price} جنيه</td>
                    <td>${appointment.paymentStatus === 'paid' ? 'مدفوع' :
                         appointment.paymentStatus === 'transferred' ? 'تم تحويله' :
                         `باقي عليه ${appointment.remainingAmount} جنيه`}</td>
                    <td>${appointment.notes || ''}</td>
                    <td>${format(appointment.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div class="no-print" style="margin-top: 20px; text-align: center;">
            <button onclick="window.print()">طباعة التقرير</button>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
  };

  const filteredAppointments = appointments.filter(appointment => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (appointment.patientName?.toLowerCase() || '').includes(searchLower) ||
      (appointment.phone || '').includes(searchQuery) ||
      (appointment.serviceType?.toLowerCase() || '').includes(searchLower)
    );
  });

  const filteredPatients = patients.filter(patient => {
    const searchLower = patientSearchQuery.toLowerCase();
    return (
      patient.name.toLowerCase().includes(searchLower) ||
      patient.phone.includes(patientSearchQuery)
    );
  });

  // التحقق إذا كان التاريخ المختار هو اليوم الحالي
  const isToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateRange.from);
    selectedDate.setHours(0, 0, 0, 0);
    
    // اعتبر اليوم الحالي فقط في فلتر "اليوم"
    return filterType === 'day' && selectedDate.getTime() === today.getTime();
  };

  // فلترة الكشوفات حسب التبويب
  const filteredAppointmentsToday = appointments.filter(app => {
    const appDate = new Date(app.createdAt);
    
    if (filterType === 'day') {
      const isCurrentDay = isToday();
      const selectedDateString = format(dateRange.from, 'yyyy-MM-dd');
      const appDateString = format(appDate, 'yyyy-MM-dd');
      
      if (isCurrentDay) {
        // في اليوم الحالي: اعرض فقط الكشوفات النشطة
        return (
          (app.queueStatus === 'waiting' || app.queueStatus === 'in-progress' || app.queueStatus === 'cancelled') &&
          appDateString === selectedDateString
        );
      } else {
        // في الأيام السابقة: اعرض جميع الكشوفات
        return appDateString === selectedDateString;
      }
    } else if (filterType === 'month') {
      // في فلتر الشهر: اعرض جميع الكشوفات من الشهر
      const selectedMonthString = format(dateRange.from, 'yyyy-MM');
      const appMonthString = format(appDate, 'yyyy-MM');
      return appMonthString === selectedMonthString;
    } else if (filterType === 'year') {
      // في فلتر السنة: اعرض جميع الكشوفات من السنة
      const selectedYearString = format(dateRange.from, 'yyyy');
      const appYearString = format(appDate, 'yyyy');
      return appYearString === selectedYearString;
    } else if (filterType === 'all') {
      // في فلتر الكل: اعرض جميع الكشوفات
      return true;
    }
    
    return false;
  }).sort((a, b) => {
    // ترتيب الكشوفات حسب الترتيب الأصلي
    const aIndex = originalAppointmentsOrder.indexOf(a.id);
    const bIndex = originalAppointmentsOrder.indexOf(b.id);
    
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    
    // إذا لم يكونا في الترتيب الأصلي، رتبهما حسب وقت الإنشاء
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  const filteredAppointmentsArchive = appointments.filter(app => {
    const appDate = new Date(app.createdAt);
    
    if (filterType === 'day') {
      const archiveDateString = format(archiveDateRange, 'yyyy-MM-dd');
      const appDateString = format(appDate, 'yyyy-MM-dd');
      return (
        app.queueStatus === 'completed' &&
        appDateString === archiveDateString
      );
    } else if (filterType === 'month') {
      const archiveMonthString = format(archiveDateRange, 'yyyy-MM');
      const appMonthString = format(appDate, 'yyyy-MM');
      return (
        app.queueStatus === 'completed' &&
        appMonthString === archiveMonthString
      );
    } else if (filterType === 'year') {
      const archiveYearString = format(archiveDateRange, 'yyyy');
      const appYearString = format(appDate, 'yyyy');
      return (
        app.queueStatus === 'completed' &&
        appYearString === archiveYearString
      );
    } else if (filterType === 'all') {
      return app.queueStatus === 'completed'; // اعرض جميع الكشوفات المكتملة
    }
    
    return false;
  }).sort((a, b) => {
    // ترتيب الكشوفات المنتهية حسب الترتيب الأصلي
    const aOriginalIndex = originalAppointmentsOrder.indexOf(a.id);
    const bOriginalIndex = originalAppointmentsOrder.indexOf(b.id);
    
    if (aOriginalIndex !== -1 && bOriginalIndex !== -1) {
      return aOriginalIndex - bOriginalIndex;
    }
    
    // إذا لم يكونا في الترتيب الأصلي، رتبهما حسب وقت الإنشاء
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  const handleAddAppointment = async () => {
    // Check only required fields
    if (!newAppointment.patientName || !newAppointment.phone || !newAppointment.serviceType) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى ملء الحقول المطلوبة (الاسم، رقم الهاتف، نوع الخدمة)"
      });
      return;
    }

    // Only check remaining amount if payment status is pending
    if (newAppointment.paymentStatus === 'pending' && newAppointment.remainingAmount === undefined) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى إدخال المبلغ المتبقي"
      });
      return;
    }

    try {
      const appointmentsRef = collection(db, 'clinicAppointments');
      const currentTime = new Date();
      const appointmentData = {
        ...newAppointment,
        date: newAppointment.date,
        time: format(currentTime, 'HH:mm'),
        createdAt: currentTime,
        remainingAmount: newAppointment.paymentStatus === 'pending' ? Number(newAppointment.remainingAmount) : 0,
        price: Number(newAppointment.price) || 0
      };
      await addDoc(appointmentsRef, appointmentData);

      setNewAppointment({
        patientName: '',
        phone: '',
        serviceType: '',
        date: new Date(),
        time: '',
        price: 0,
        paymentStatus: 'pending',
        remainingAmount: 0,
        notes: ''
      });
      setIsAddingAppointment(false);
      toast({
        title: "تمت الإضافة",
        description: "تم إضافة الموعد بنجاح"
      });
      fetchAppointments();
    } catch (error) {
      console.error('Error adding appointment:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء إضافة الموعد"
      });
    }
  };

  const calculateRevenue = (appointments: Appointment[]) => {
    return appointments.reduce((total, appointment) => total + appointment.price, 0);
  };

  const getRevenueStats = () => {
    const todayRevenue = calculateRevenue(
      appointments.filter(app => 
        format(app.createdAt, 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')
      )
    );

    const monthRevenue = calculateRevenue(
      appointments.filter(app => 
        format(app.createdAt, 'yyyy-MM') === format(currentDate, 'yyyy-MM')
      )
    );

    const yearRevenue = calculateRevenue(
      appointments.filter(app => 
        format(app.createdAt, 'yyyy') === format(currentDate, 'yyyy')
      )
    );

    return { todayRevenue, monthRevenue, yearRevenue };
  };

  const { todayRevenue, monthRevenue, yearRevenue } = getRevenueStats();

  const handlePaymentStatusChange = async (appointmentId: string, newStatus: 'paid' | 'pending' | 'transferred') => {
    try {
      const appointmentRef = doc(db, 'clinicAppointments', appointmentId);
      await updateDoc(appointmentRef, { paymentStatus: newStatus });
      toast({
        title: "تم التحديث",
        description: "تم تحديث حالة الدفع بنجاح"
      });
      fetchAppointments();
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث حالة الدفع"
      });
    }
  };

  const handleDoctorNotesChange = async (appointmentId: string, notes: string) => {
    try {
      const appointmentRef = doc(db, 'clinicAppointments', appointmentId);
      await updateDoc(appointmentRef, { doctorNotes: notes });
      toast({
        title: "تم التحديث",
        description: "تم تحديث ملاحظات الطبيب بنجاح"
      });
      fetchAppointments();
    } catch (error) {
      console.error('Error updating doctor notes:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث ملاحظات الطبيب"
      });
    }
  };

  const handleDelete = async (appointmentId: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا الكشف؟')) return;

    try {
      const appointmentRef = doc(db, 'clinicAppointments', appointmentId);
      await deleteDoc(appointmentRef);
      toast({
        title: "تم الحذف",
        description: "تم حذف الكشف بنجاح"
      });
      fetchAppointments();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء حذف الكشف"
      });
    }
  };

  const handleEditAppointment = async () => {
    if (!editingAppointment) return;

    if (!editingAppointment.patientName || !editingAppointment.phone || !editingAppointment.serviceType || editingAppointment.price === undefined) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى ملء جميع الحقول المطلوبة"
      });
      return;
    }

    if (editingAppointment.paymentStatus === 'pending' && (editingAppointment.remainingAmount === undefined || editingAppointment.remainingAmount === null)) {
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "يرجى إدخال المبلغ المتبقي"
      });
      return;
    }

    try {
      const appointmentRef = doc(db, 'clinicAppointments', editingAppointment.id);
      await updateDoc(appointmentRef, {
        ...editingAppointment,
        price: Number(editingAppointment.price),
        remainingAmount: editingAppointment.paymentStatus === 'pending' ? Number(editingAppointment.remainingAmount) : 0,
      });
      
      setEditingAppointment(null);
      toast({
        title: "تم التحديث",
        description: "تم تحديث الكشف بنجاح"
      });
      
      fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث الكشف"
      });
    }
  };

  const handleQueueStatusChange = async (appointmentId: string, newStatus: 'waiting' | 'in-progress' | 'completed' | 'cancelled') => {
    try {
      const appointmentRef = doc(db, 'clinicAppointments', appointmentId);
      await updateDoc(appointmentRef, { queueStatus: newStatus });
      
      // إذا تم إنهاء الكشف، أضف رسالة تأكيد
      if (newStatus === 'completed') {
        toast({
          title: "تم إنهاء الكشف",
          description: "تم نقل الكشف للأرشيف بنجاح"
        });
      } else {
        toast({
          title: "تم التحديث",
          description: "تم تحديث حالة الدور بنجاح"
        });
      }
      
      fetchAppointments();
    } catch (error) {
      console.error('Error updating queue status:', error);
      toast({
        variant: "destructive",
        title: "خطأ",
        description: "حدث خطأ أثناء تحديث حالة الدور"
      });
    }
  };

  // التحقق من إمكانية عرض الأرشيف (فقط في اليوم الحالي)
  const canShowArchive = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(dateRange.from);
    selectedDate.setHours(0, 0, 0, 0);
    
    // اعرض الأرشيف فقط في فلتر "اليوم" عندما يكون اليوم الحالي
    return filterType === 'day' && selectedDate.getTime() === today.getTime();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <Tabs value={activeTab} onValueChange={value => setActiveTab(value as 'today' | 'archive')} className="mb-6">
          <TabsList>
            <TabsTrigger value="today">كشوفات اليوم</TabsTrigger>
            {canShowArchive() && (
              <TabsTrigger value="archive">الأرشيف (المنتهية)</TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {activeTab === 'today' && (
          <>
            {/* شريط الأدوات العلوي: بحث + فلاتر + زر إضافة */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-4">
              {/* مجموعة البحث والفلاتر */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-4 w-full">
                {/* مربع البحث */}
                <div className="flex items-center gap-2 relative group w-full sm:w-auto">
                  <Input
                    placeholder="بحث عن مريض..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full sm:w-[200px] transition-all duration-300 focus:w-[250px]"
                  />
                  <Search className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors absolute right-2 top-1/2 -translate-y-1/2" />
                </div>
                {/* فلتر Select */}
                <Select 
                  value={filterType} 
                  onValueChange={(value: 'day' | 'month' | 'year' | 'all') => {
                    setFilterType(value);
                    const today = new Date();
                    if (value === 'day') {
                      setDateRange({ from: startOfDay(today), to: endOfDay(today) });
                    } else if (value === 'month') {
                      setDateRange({ 
                        from: startOfMonth(today), 
                        to: endOfMonth(today) 
                      });
                    } else if (value === 'year') {
                      setDateRange({ 
                        from: startOfYear(today), 
                        to: endOfYear(today) 
                      });
                    } else if (value === 'all') {
                      setDateRange({ from: new Date(0), to: new Date() });
                    }
                  }}
                >
                  <SelectTrigger className="w-full sm:w-[180px] hover:border-primary transition-colors">
                    <SelectValue placeholder="اختر الفلتر" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">اليوم</SelectItem>
                    <SelectItem value="month">الشهر</SelectItem>
                    <SelectItem value="year">السنة</SelectItem>
                    <SelectItem value="all">الكل</SelectItem>
                  </SelectContent>
                </Select>
                {/* زر التقويم (Popover) فقط */}
                <Popover open={isDatePopoverOpen} onOpenChange={setIsDatePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-[200px] justify-start text-left font-normal hover:border-primary transition-colors" 
                      disabled={filterType === 'all'}
                    >
                      <CalendarIcon className="ml-2 h-4 w-4" />
                      {filterType === 'all' ? 'جميع التواريخ' : format(dateRange.from, 'dd MMMM yyyy', { locale: ar })}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    {/* تحكم السنة */}
                    <div className="flex items-center justify-center gap-2 p-2">
                      <label htmlFor="year-select" className="text-sm">السنة:</label>
                      <select
                        id="year-select"
                        className="border rounded px-2 py-1 text-sm"
                        value={calendarYear}
                        onChange={e => {
                          const newYear = parseInt(e.target.value, 10);
                          setCalendarYear(newYear);
                          setDateRange(({from, to}) => {
                            const newDate = new Date(from);
                            newDate.setFullYear(newYear);
                            return { from: newDate, to: newDate };
                          });
                        }}
                      >
                        {Array.from({length: 15}, (_, i) => {
                          const year = new Date().getFullYear() - 7 + i;
                          return <option key={year} value={year}>{year}</option>;
                        })}
                      </select>
                    </div>
                    <Calendar
                      mode="single"
                      selected={dateRange.from}
                      onSelect={(date) => {
                        if (date) {
                          setDateRange({ from: date, to: date });
                          setFilterType('day');
                          setIsDatePopoverOpen(false);
                        }
                      }}
                      month={dateRange.from}
                      onMonthChange={date => {
                        setDateRange(({from, to}) => ({ from: date, to: date }));
                        setCalendarYear(date.getFullYear());
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {/* زر إضافة كشف جديد */}
              <div className="flex justify-end mt-2 md:mt-0">
                <Dialog open={isAddingAppointment} onOpenChange={setIsAddingAppointment}>
                  <DialogTrigger asChild>
                    <Button className="flex items-center gap-2 hover:scale-105 transition-transform px-6 py-2 text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
                      <Plus className="w-5 h-5" />
                      إضافة كشف
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        إضافة كشف جديد
                      </DialogTitle>
                      <DialogDescription className="text-muted-foreground">
                        قم بملء البيانات المطلوبة لإضافة كشف جديد
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label>اسم المريض</Label>
                        <Input
                          value={newAppointment.patientName || ''}
                          onChange={(e) => setNewAppointment({ ...newAppointment, patientName: e.target.value })}
                          placeholder="أدخل اسم المريض"
                        />
                      </div>
                      <div className="grid gap-2">
                        <Label>رقم الهاتف</Label>
                        <Input
                          value={newAppointment.phone || ''}
                          onChange={(e) => setNewAppointment({ ...newAppointment, phone: e.target.value })}
                          placeholder="أدخل رقم الهاتف"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label>نوع الخدمة</Label>
                        <Select
                          value={newAppointment.serviceType || ''}
                          onValueChange={(value) => setNewAppointment({ ...newAppointment, serviceType: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="اختر نوع الخدمة" />
                          </SelectTrigger>
                          <SelectContent>
                            {serviceTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-2">
                        <Label>السعر</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={newAppointment.price || ''}
                          onChange={(e) => setNewAppointment({ 
                            ...newAppointment, 
                            price: e.target.value ? Number(e.target.value) : 0 
                          })}
                          placeholder="أدخل سعر الكشف"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label>حالة الدفع</Label>
                        <Select
                          value={newAppointment.paymentStatus || 'pending'}
                          onValueChange={(value: 'paid' | 'pending' | 'transferred') =>
                            setNewAppointment({ ...newAppointment, paymentStatus: value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="اختر حالة الدفع" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paid">مدفوع</SelectItem>
                            <SelectItem value="pending">باقي عليه</SelectItem>
                            <SelectItem value="transferred">تم تحويله</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {newAppointment.paymentStatus === 'pending' && (
                        <div className="grid gap-2">
                          <Label>المبلغ المتبقي <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={newAppointment.remainingAmount || ''}
                            onChange={(e) => {
                              const value = e.target.value ? parseFloat(e.target.value) : 0;
                              setNewAppointment({ 
                                ...newAppointment, 
                                remainingAmount: value
                              });
                            }}
                            placeholder="أدخل المبلغ المتبقي"
                            required
                          />
                        </div>
                      )}

                      <div className="grid gap-2">
                        <Label>ملاحظات</Label>
                        <Textarea
                          value={newAppointment.notes || ''}
                          onChange={(e) => setNewAppointment({ ...newAppointment, notes: e.target.value })}
                          placeholder="أدخل أي ملاحظات إضافية"
                          className="min-h-[100px]"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-4">
                      <Button variant="outline" onClick={() => setIsAddingAppointment(false)}>
                        إلغاء
                      </Button>
                      <Button onClick={handleAddAppointment}>
                        إضافة الكشف
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">إجمالي الكشوفات</CardTitle>
                  <BarChart3 className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{statistics.totalAppointments}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {filterType === 'day' ? 'اليوم' : 
                     filterType === 'month' ? 'هذا الشهر' : 
                     filterType === 'year' ? 'هذا العام' : 'الكل'}
                  </p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">إجمالي الإيرادات</CardTitle>
                  <DollarSign className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{statistics.totalRevenue.toFixed(2)} جنيه</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {filterType === 'day' ? 'اليوم' : 
                     filterType === 'month' ? 'هذا الشهر' : 
                     filterType === 'year' ? 'هذا العام' : 'الكل'}
                  </p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">متوسط سعر الكشف</CardTitle>
                  <TrendingUp className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{statistics.averagePrice.toFixed(2)} جنيه</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {filterType === 'day' ? 'اليوم' : 
                     filterType === 'month' ? 'هذا الشهر' : 
                     filterType === 'year' ? 'هذا العام' : 'الكل'}
                  </p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">أعلى خدمة</CardTitle>
                  <Stethoscope className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{statistics.topService}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {statistics.topServiceCount} كشف
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Queue Statistics - Only show for today */}
            {filterType === 'day' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <Card className="hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-yellow-500">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">في الانتظار</CardTitle>
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">
                      {filteredAppointmentsToday.length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">في انتظار المعالجة</p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-green-500">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">مكتمل</CardTitle>
                    <UserCheck className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {appointments.filter(app => {
                        const appDate = new Date(app.createdAt);
                        const today = new Date();
                        return (
                          app.queueStatus === 'completed' &&
                          appDate.getFullYear() === today.getFullYear() &&
                          appDate.getMonth() === today.getMonth() &&
                          appDate.getDate() === today.getDate()
                        );
                      }).length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">تم إنهاؤه</p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-red-500">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">ملغي</CardTitle>
                    <UserX className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      {filteredAppointmentsToday.filter(app => app.queueStatus === 'cancelled').length}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">تم إلغاؤه</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Export Buttons */}
            <div className="flex justify-end gap-4 mb-6">
              <Button 
                variant="outline" 
                onClick={exportToExcel} 
                className="flex items-center gap-2 hover:bg-primary/10 transition-colors"
              >
                <Download className="w-4 h-4" />
                تصدير Excel
              </Button>
              <Button 
                variant="outline" 
                onClick={printReport} 
                className="flex items-center gap-2 hover:bg-primary/10 transition-colors"
              >
                <Printer className="w-4 h-4" />
                طباعة التقرير
              </Button>
            </div>

            {/* Appointments List */}
            <Card className="shadow-modern">
              <CardContent className="pt-6">
                {/* رسالة توضيحية - فقط في فلتر اليوم الحالي */}
                {isToday() && filterType === 'day' && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      💡 <strong>ملاحظة:</strong> الكشوفات المنتهية (المكتملة) موجودة في تبويب "الأرشيف (المنتهية)"
                    </p>
                  </div>
                )}
                
                <div className="grid gap-6">
                  {filteredAppointmentsToday.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      لا توجد كشوفات في هذه الفترة
                    </div>
                  ) : (
                    filteredAppointmentsToday.map((appointment, index) => {
                      // حساب رقم الدور للكشوفات اليوم فقط
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const appointmentDate = new Date(appointment.date);
                      const isToday = appointmentDate >= today;
                      const isCurrentDay = isToday;
                      const queueNumber = isToday ? index + 1 : null;
                      const queueStatus = appointment.queueStatus || 'waiting';

                      return (
                        <Card 
                          key={appointment.id} 
                          className="hover:shadow-lg transition-all duration-300 hover:scale-[1.02] w-full"
                        >
                          <CardHeader className="pb-2">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                              <div className="flex items-center gap-3 w-full">
                                {isToday && isCurrentDay && filterType === 'day' && (
                                  <div className="bg-blue-100 text-blue-600 p-1.5 rounded-full font-bold text-base min-w-[32px] text-center">
                                    {queueNumber}
                                  </div>
                                )}
                                <div className="bg-primary/10 p-2 rounded-full">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                  <CardTitle className="text-sm sm:text-base font-semibold">
                                    {appointment.patientName}
                                  </CardTitle>
                                  <p className="text-xs sm:text-sm text-muted-foreground">
                                    {appointment.serviceType}
                                  </p>
                                  {isToday && isCurrentDay && filterType === 'day' && (
                                    <Badge 
                                      variant={
                                        queueStatus === 'waiting' ? 'secondary' :
                                        queueStatus === 'in-progress' ? 'default' :
                                        queueStatus === 'completed' ? 'outline' :
                                        'destructive'
                                      }
                                      className="text-[10px] sm:text-xs mt-1"
                                    >
                                      {queueStatus === 'waiting' ? 'في الانتظار' :
                                       queueStatus === 'in-progress' ? 'قيد المعالجة' :
                                       queueStatus === 'completed' ? 'مكتمل' : 'ملغي'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-2 sm:mt-0">
                                <Badge
                                  variant={
                                    appointment.paymentStatus === 'paid'
                                      ? 'default'
                                      : appointment.paymentStatus === 'transferred'
                                      ? 'secondary'
                                      : 'destructive'
                                  }
                                  className="text-[10px] sm:text-xs"
                                >
                                  {appointment.paymentStatus === 'paid'
                                    ? 'مدفوع'
                                    : appointment.paymentStatus === 'transferred'
                                    ? 'تم تحويله'
                                    : `باقي عليه ${appointment.remainingAmount} جنيه`}
                                </Badge>
                                {/* أزرار تحكم الدور للكشوفات اليوم فقط */}
                                {isToday && isCurrentDay && filterType === 'day' && (
                                  <div className="flex flex-wrap items-center gap-1 mt-1">
                                    {queueStatus === 'waiting' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleQueueStatusChange(appointment.id, 'in-progress')}
                                        className="flex items-center gap-1 text-[10px] sm:text-xs px-2 py-1"
                                      >
                                        <UserCheck className="w-3 h-3" />
                                        بدء
                                      </Button>
                                    )}
                                    {queueStatus === 'in-progress' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleQueueStatusChange(appointment.id, 'completed')}
                                        className="flex items-center gap-1 text-[10px] sm:text-xs px-2 py-1"
                                      >
                                        <UserCheck className="w-3 h-3" />
                                        إنهاء
                                      </Button>
                                    )}
                                    {(queueStatus === 'waiting' || queueStatus === 'in-progress') && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleQueueStatusChange(appointment.id, 'cancelled')}
                                        className="flex items-center gap-1 text-[10px] sm:text-xs text-red-600 px-2 py-1"
                                      >
                                        <UserX className="w-3 h-3" />
                                        إلغاء
                                      </Button>
                                    )}
                                    {queueStatus === 'cancelled' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleQueueStatusChange(appointment.id, 'waiting')}
                                        className="flex items-center gap-1 text-[10px] sm:text-xs px-2 py-1"
                                      >
                                        <UserCheck className="w-3 h-3" />
                                        إعادة تفعيل
                                      </Button>
                                    )}
                                  </div>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setEditingAppointment(appointment)}
                                  className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-primary/10 transition-colors"
                                >
                                  <Pencil className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(appointment.id)}
                                  className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                >
                                  <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
                              <div className="flex items-center gap-2">
                                <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
                                <span>{appointment.phone}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground" />
                                <span>{appointment.price} جنيه</span>
                              </div>
                              {appointment.notes && (
                                <div className="col-span-1 sm:col-span-2 flex items-start gap-2">
                                  <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-muted-foreground mt-1" />
                                  <span className="text-[10px] sm:text-xs text-muted-foreground line-clamp-2">
                                    {appointment.notes}
                                  </span>
                                </div>
                              )}
                              <div className="col-span-1 sm:col-span-2 text-[10px] sm:text-xs text-muted-foreground">
                                {format(appointment.createdAt, 'dd MMMM yyyy - hh:mm a', { locale: ar })}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'archive' && (
          <>
            {/* فلتر تاريخ الأرشيف */}
            <div className="flex items-center gap-4 mb-6">
              <Label>اختر تاريخ الأرشيف:</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-[200px] justify-start text-left font-normal hover:border-primary transition-colors"
                  >
                    <CalendarIcon className="ml-2 h-4 w-4" />
                    {format(archiveDateRange, 'dd MMMM yyyy', { locale: ar })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={archiveDateRange}
                    onSelect={(date) => {
                      if (date) {
                        setArchiveDateRange(date);
                      }
                    }}
                    month={archiveDateRange}
                    onMonthChange={date => setArchiveDateRange(date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Card className="shadow-modern">
              <CardHeader>
                <CardTitle>الأرشيف - الكشوفات المنتهية ({format(archiveDateRange, 'dd MMMM yyyy', { locale: ar })})</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid gap-6">
                  {filteredAppointmentsArchive.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      لا يوجد مرضى مكتملين في هذا اليوم
                    </div>
                  ) : (
                    filteredAppointmentsArchive.map((appointment, index) => (
                      <Card key={appointment.id} className="border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="text-2xl font-bold text-green-600">
                              {index + 1}
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{appointment.patientName}</h3>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {appointment.phone}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Stethoscope className="w-3 h-3" />
                                  {appointment.serviceType}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {format(new Date(appointment.createdAt), 'HH:mm', { locale: ar })}
                                </span>
                              </div>
                              {appointment.notes && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {appointment.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Queue System Dialog */}
        <Dialog open={showQueueSystem} onOpenChange={setShowQueueSystem}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                نظام إدارة الدور
              </DialogTitle>
            </DialogHeader>
            <QueueSystem appointments={appointments} />
          </DialogContent>
        </Dialog>

        {/* Edit Appointment Dialog */}
        <Dialog open={!!editingAppointment} onOpenChange={() => setEditingAppointment(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                تعديل الكشف
              </DialogTitle>
            </DialogHeader>
            {editingAppointment && (
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label>اسم المريض</Label>
                  <Input
                    value={editingAppointment.patientName}
                    onChange={(e) => setEditingAppointment({ ...editingAppointment, patientName: e.target.value })}
                    placeholder="أدخل اسم المريض"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>رقم الهاتف</Label>
                  <Input
                    value={editingAppointment.phone}
                    onChange={(e) => setEditingAppointment({ ...editingAppointment, phone: e.target.value })}
                    placeholder="أدخل رقم الهاتف"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>نوع الخدمة</Label>
                  <Select
                    value={editingAppointment.serviceType}
                    onValueChange={(value) => setEditingAppointment({ ...editingAppointment, serviceType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر نوع الخدمة" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>السعر</Label>
                  <Input
                    type="number"
                    value={editingAppointment.price === 0 ? '' : editingAppointment.price}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditingAppointment({ ...editingAppointment, price: val === '' ? 0 : Number(val) });
                    }}
                    placeholder="أدخل سعر الكشف"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>حالة الدفع</Label>
                  <Select
                    value={editingAppointment.paymentStatus}
                    onValueChange={(value: 'paid' | 'pending' | 'transferred') =>
                      setEditingAppointment({ ...editingAppointment, paymentStatus: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر حالة الدفع" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">مدفوع</SelectItem>
                      <SelectItem value="pending">باقي عليه</SelectItem>
                      <SelectItem value="transferred">تم تحويله</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingAppointment.paymentStatus === 'pending' && (
                  <div className="grid gap-2">
                    <Label>المبلغ المتبقي <span className="text-red-500">*</span></Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editingAppointment.remainingAmount || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseFloat(e.target.value) : 0;
                        setEditingAppointment({ 
                          ...editingAppointment, 
                          remainingAmount: value
                        });
                      }}
                      placeholder="أدخل المبلغ المتبقي"
                      required
                    />
                  </div>
                )}
                <div className="grid gap-2">
                  <Label>ملاحظات</Label>
                  <Textarea
                    value={editingAppointment.notes || ''}
                    onChange={(e) => setEditingAppointment({ ...editingAppointment, notes: e.target.value })}
                    placeholder="أدخل أي ملاحظات إضافية"
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={() => setEditingAppointment(null)}>
                إلغاء
              </Button>
              <Button onClick={handleEditAppointment}>
                حفظ التغييرات
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
} 